import { Component, effect, Signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GenericComponent } from '../generic/generic.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { Store } from '@ngxs/store';
import { ConcessioncontrolState, PrepareDataFromKeys } from '../../states/concessioncontrol.state';
import { combineLatest, distinctUntilChanged } from 'rxjs';
import { CC_CONSTANTS } from '../../assets/constants';
import { ApexAxisChartSeries, ApexDataLabels, ApexXAxis, ApexYAxis } from 'ng-apexcharts';
import { colorAec3Green, colorAec7RedLight } from 'src/app/shared/all.constants';
import { EXPENSES_MAINTENANCE_OPEX_EVOL_EXPENDITURE_CHART } from '../../assets/keys';

const KEYS = [EXPENSES_MAINTENANCE_OPEX_EVOL_EXPENDITURE_CHART];

interface ChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    // kpi_id=952
    amountWorks?: number;
    //kpi_id=951
    lenghtNetworks?: number;
    error?: boolean;
}

@UntilDestroy()
@Component({
    selector: 'app-expenses-maintenance',
    templateUrl: './expenses-maintenance.component.html',
    styleUrls: ['./expenses-maintenance.component.scss'],
})
export class ExpensesMaintenanceComponent extends GenericComponent {
    public texts = CC_CONSTANTS.maintenanceCost.texts;

    public chartData: Signal<ChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(EXPENSES_MAINTENANCE_OPEX_EVOL_EXPENDITURE_CHART),
    ) as Signal<ChartData[]>;
    public chartSeries: ApexAxisChartSeries = [];
    public dataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatter([0, 1, 2, 3, 4, 5, 6]),
    };
    public xaxisCategories: ApexXAxis['categories'];
    public yaxis: ApexYAxis[] = [
        {
            seriesName: this.texts.evolutionExpenditureChart.amountWorksAxisTitle,
            min: 0,
            title: {
                text: this.texts.evolutionExpenditureChart.yaxisTitleLeft,
            },
        },
        {
            seriesName: this.texts.evolutionExpenditureChart.lenghtNetworksAxisTitle,
            min: 0,
            opposite: true,
            title: {
                text: this.texts.evolutionExpenditureChart.yaxisTitleRight,
            },
        },
    ];
    public chartError: boolean = true;

    constructor(
        private readonly route: ActivatedRoute,
        private readonly sidenavService: SidenavService,
        private readonly store: Store,
    ) {
        super(route, sidenavService, store);

        effect(() => {
            const dataList = this.chartData();
            this.chartError = this.checkForError(dataList, [
                'xYear',
                'amountWorks',
                'lenghtNetworks',
            ]);
            if (!this.chartError) {
                this.xaxisCategories = dataList.map((data) => data.xYear);
                this.chartSeries = [
                    {
                        name: this.texts.evolutionExpenditureChart.amountWorksAxisTitle,
                        type: 'column',
                        data: dataList.map((data) => data.amountWorks!),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.evolutionExpenditureChart.lenghtNetworksAxisTitle,
                        type: 'line',
                        data: dataList.map((data) => data.lenghtNetworks!),
                        color: colorAec7RedLight,
                    },
                ];
                this.dataLabels.enabledOnSeries = [0, 1];
            }
        });
    }

    ngOnInit() {
        // Every time currentOrganization or currentYear changes, we tell the store he has to ask for the data
        combineLatest([this._currentOrganization$, this._currentYear$])
            .pipe(untilDestroyed(this), distinctUntilChanged())
            .subscribe(() => {
                this.store.dispatch(new PrepareDataFromKeys({ keys: KEYS }));
            });
    }
}
